import React, { FC, PropsWithChildren, useMemo } from 'react'

import { css, cx } from '@linaria/core'
import { styled } from '@linaria/react'
import { useLocation } from 'react-router'

import { SidebarSwitch } from 'components/block/Sidebar/SidebarSwitch'
import { breakpoints } from 'components/designSystem/shared/breakpoints'
import { ContainerSize } from 'components/designSystem/shared/containerSize'
import { SidebarSize } from 'components/designSystem/shared/sidebar'
import { useCacheSvg } from 'components/page/RatingWave/hooks/useCacheSvg'
import { ratingPath } from 'components/page/RatingWave/Rating.paths'
import { profilePath } from 'components/paths'
import { useHasUserRestrictions } from 'functions/hasUserRestrictions'
import { removeLocaleFromPath2 } from 'functions/language'
import { useAuthorized } from 'hooks/useAuthorized'
import { useCurrentUserRouteMatch } from 'hooks/useCurrentUserRouteMatch'
import { useIsScreenHeightLayout } from 'hooks/useIsScreenHeightLayout'
import { useLoveMailruPartner } from 'hooks/useLoveMailruPartner'

import { ClientRender } from '../../../../../client/ClientRender'

export const MAIN_LAYOUT_ID = 'main-layout' as const

export const WaveLayoutMainContainer: FC<PropsWithChildren> = ({
  children,
}) => {
  useCacheSvg()

  const { pathname } = useLocation()
  const _pathname = removeLocaleFromPath2(pathname)
  const isCurrentUserPath = useCurrentUserRouteMatch(_pathname)
  const authorized = useAuthorized()
  const isLoveMailruPartner = useLoveMailruPartner()
  const userHasRestrictions = useHasUserRestrictions()
  const isScreenHeightLayout = useIsScreenHeightLayout()

  const fillHeight = useMemo(() => {
    const pathnameAccept =
      _pathname.startsWith(ratingPath) ||
      (_pathname.startsWith(profilePath) && !isCurrentUserPath)

    /**
     * на mobile для love.mail есть доп.меню "бургер"
     */
    if (pathnameAccept) {
      if (authorized) {
        if (isLoveMailruPartner) {
          return ratingStyleMailruAuthorized
        }
        return ratingStyleAuthorized
      }

      if (isLoveMailruPartner) {
        return ratingStyleMailruNotAuthorized
      }

      return ratingStyleNotAuthorized
    }
  }, [_pathname, isCurrentUserPath, authorized, isLoveMailruPartner])

  const parentClassNames = cx(
    fillHeight,
    isScreenHeightLayout && authorized && screenHeightLayoutCss
  )
  const mainClassNames = cx(
    isScreenHeightLayout ? screenHeightLayoutPaddingCss : layoutPadding
  )

  if (userHasRestrictions) {
    return (
      <Parent className={parentClassNames} id={MAIN_LAYOUT_ID}>
        <Main className={mainClassNames}>{children}</Main>
      </Parent>
    )
  }

  return (
    <Parent className={parentClassNames} id={MAIN_LAYOUT_ID}>
      <Sidebar id="sidebar">
        <ClientRender>
          <SidebarSwitch />
        </ClientRender>
      </Sidebar>
      <Main className={mainClassNames}>{children}</Main>
    </Parent>
  )
}

/**
 * Пока пришлось вернуть flex, с grid есть проблема
 * https://youtrack.mamba.ru/issue/M-11092
 * Почему то остается старая высота
 */
const Parent = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: space-between;
  width: 100%;

  @media (min-width: ${breakpoints.mobile + 1}px) {
    width: ${ContainerSize.tablet}px;
    margin: var(--spacing-16px) 0 0 0;
  }

  @media (min-width: ${breakpoints.startDesktop}px) {
    margin: var(--spacing-16px) 0 var(--spacing-32px) 0;
    width: ${ContainerSize.start}px;
    gap: var(--spacing-32px);
  }

  @media (min-width: ${breakpoints.middleDesktop}px) {
    width: ${ContainerSize.middle}px;
  }

  @media (min-width: ${breakpoints.largeDesktop}px) {
    margin: var(--spacing-16px) 0 var(--spacing-80px) 0;
    width: ${ContainerSize.large}px;
  }
`

/**
 * TODO: если не найдут баг с flexbox-gap,
 * то через месяц удалить
 * Если найдут по поставить между Sidebar и Main
 */
const _Separator = styled.div`
  min-width: var(--spacing-32px);

  @media (max-width: ${breakpoints.middleDesktop - 1}px) {
    display: none;
  }
`

/**
 * Высота навигации(сверху, снизу) + у каждой навигации border в 1px
 * Почему то в linaria в функции css не работает template variables
 */
const height = `
  height: calc(100vh - var(--spacing-48px) * 2);
  height: calc(100dvh - var(--spacing-48px) * 2);
`

const heightNotAuthorized = `
  height: calc(100vh - var(--spacing-48px) * 2 - var(--spacing-16px) - var(--spacing-80px));
  height: calc(100dvh - var(--spacing-48px) * 2 - var(--spacing-16px) - var(--spacing-80px));
`

// 48 высота сверху снизу панелей + бургер меню с лого
const heightMailru = `
  height: calc(100vh - var(--spacing-48px) * 3);
  height: calc(100dvh - var(--spacing-48px) * 3);
`
// 56 высота кнопок для неавторизованного + отступ сверху и снизу + бургер меню с лого
const heightMailruNotAuthorized = `
  height: calc(100vh - var(--spacing-48px) * 2 - var(--spacing-56px) - var(--spacing-24px));
  height: calc(100dvh - var(--spacing-48px) * 2 - var(--spacing-56px) - var(--spacing-24px));
`

const ratingStyleAuthorized = css`
  @media screen and (max-width: ${breakpoints.mobile}px) {
    ${height}
  }
`
const ratingStyleNotAuthorized = css`
  @media screen and (max-width: ${breakpoints.mobile}px) {
    ${heightNotAuthorized}
  }
`

const ratingStyleMailruAuthorized = css`
  @media screen and (max-width: ${breakpoints.mobile}px) {
    ${heightMailru}
  }
`
const ratingStyleMailruNotAuthorized = css`
  @media screen and (max-width: ${breakpoints.mobile}px) {
    ${heightMailruNotAuthorized}
  }
`

const Sidebar = styled.aside`
  display: flex;
  height: min-content;
  position: sticky;

  /**
    * var(--spacing-48px) - высота  навигации
    * var(--spacing-24px) - отступ сверху у навигации
    * var(--spacing-16px) * 2 - отступ снизу у навигации и основного контента
  */
  top: calc(
    var(--spacing-48px) + var(--spacing-24px) + var(--spacing-16px) * 2
  );

  @media screen and (max-width: ${breakpoints.startDesktop - 1}px) {
    display: none;
  }

  @media screen and (min-width: ${breakpoints.startDesktop}px) {
    min-width: ${SidebarSize.middle}px;
    max-width: ${SidebarSize.middle}px;
  }

  @media screen and (min-width: ${breakpoints.largeDesktop}px) {
    /**
      * var(--spacing-48px) - высота  навигации
      * var(--spacing-64px) - отступ сверху у навигации
      * var(--spacing-16px) * 2 - отступ снизу у навигации и основного контента
    */
    top: calc(
      var(--spacing-48px) + var(--spacing-64px) + var(--spacing-16px) * 2
    );
    min-width: ${SidebarSize.large}px;
    max-width: ${SidebarSize.large}px;
  }
`

const Main = styled.section`
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  /** Почему то без этого flex разъезжается */
  min-width: 0;
  min-height: 100%;
  max-height: 100%;
`

const layoutPadding = css`
  padding: 0 var(--spacing-12px);

  @media screen and (min-width: ${breakpoints.mobile + 1}px) {
    padding: 0;
  }
`
const screenHeightLayoutPaddingCss = css`
  padding: 0 var(--spacing-12px);

  @media screen and (min-width: ${breakpoints.mobile + 1}px) {
    padding: 0 0 var(--spacing-12px);
  }
`
const screenHeightLayoutCss = css`
  overflow-y: auto;
`
